import { Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense } from "react";
import { Spin } from "antd";
import type { Permission } from "@salon/shared";
import AppShell from "./components/layout/AppShell";
import RequireAuth from "./components/layout/RequireAuth";
import LoginPage from "./routes/login";
import InitPage from "./routes/init";

const EmployeesPage = lazy(() => import("./routes/employees"));
const CustomersPage = lazy(() => import("./routes/customers"));
const ServicesPage = lazy(() => import("./routes/services"));
const ProductsPage = lazy(() => import("./routes/products"));
const InventoryPage = lazy(() => import("./routes/inventory"));
const CommissionsPage = lazy(() => import("./routes/commissions"));
const PosRoute = lazy(() => import("./routes/pos"));
const OrdersPage = lazy(() => import("./routes/orders"));
const AppointmentsPage = lazy(() => import("./routes/appointments"));
const MeAppointmentsPage = lazy(() => import("./routes/me-appointments"));
const MeCommissionsPage = lazy(() => import("./routes/me-commissions"));
const ReportsPage = lazy(() => import("./routes/reports"));
const SettingsPage = lazy(() => import("./routes/settings"));
const UsersPage = lazy(() => import("./routes/users"));
const AuditLogPage = lazy(() => import("./routes/audit-log"));

const Loading = () => (
  <div style={{ display: "grid", placeItems: "center", padding: 64 }}>
    <Spin size="large" />
  </div>
);

const protectedPage = (Component: React.ComponentType, permission?: Permission) => (
  <RequireAuth permission={permission}>
    <Suspense fallback={<Loading />}>
      <Component />
    </Suspense>
  </RequireAuth>
);

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/init" element={<InitPage />} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route path="/" element={<Navigate to="/appointments" replace />} />
        <Route path="/dashboard" element={<Navigate to="/appointments" replace />} />
        <Route
          path="/appointments"
          element={protectedPage(AppointmentsPage, "appointments.view")}
        />
        <Route path="/me/appointments" element={protectedPage(MeAppointmentsPage)} />
        <Route path="/me/commissions" element={protectedPage(MeCommissionsPage)} />
        <Route path="/pos" element={protectedPage(PosRoute, "pos.view")} />
        {/* Backwards-compat: redirect cũ → /pos */}
        <Route path="/pos/service" element={<Navigate to="/pos" replace />} />
        <Route path="/pos/product" element={<Navigate to="/pos" replace />} />
        <Route path="/orders" element={protectedPage(OrdersPage, "orders.view")} />
        <Route path="/employees" element={protectedPage(EmployeesPage, "employees.view")} />
        <Route path="/customers" element={protectedPage(CustomersPage, "customers.view")} />
        <Route path="/services" element={protectedPage(ServicesPage, "services.view")} />
        <Route path="/products" element={protectedPage(ProductsPage, "products.view")} />
        <Route path="/inventory" element={protectedPage(InventoryPage, "inventory.view")} />
        <Route path="/commissions" element={protectedPage(CommissionsPage, "commissions.view")} />
        <Route
          path="/commissions/services"
          element={<Navigate to="/commissions?tab=service" replace />}
        />
        <Route
          path="/commissions/products"
          element={<Navigate to="/commissions?tab=product" replace />}
        />
        <Route path="/reports" element={protectedPage(ReportsPage, "reports.view")} />
        <Route path="/users" element={protectedPage(UsersPage, "users.view")} />
        <Route path="/audit-log" element={protectedPage(AuditLogPage, "audit.view")} />
        <Route path="/settings" element={protectedPage(SettingsPage, "settings.view")} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
